import { useQuery } from '@tanstack/react-query'
import { AlertCircle, FileText, TrendingDown, TrendingUp, User } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { api, getApiKey } from '../api'
import { CoachingTab, fetchCoaching } from '../components/CoachingTab'
import { DispoBadge, DispoMismatchBadge } from '../components/DispoBadge'
import { GateFailures } from '../components/GateFailures'
import { MetricTrendChart } from '../components/MetricTrendChart'
import { PassRateBar, ScoreBar } from '../components/ScoreBar'
import { TierBadge } from '../components/TierBadge'
import type { AgentSummary, CallRow, Tier } from '../types'

const METRIC_ORDER = [
  'talk_ratio',
  'longest_monologue',
  'next_step_commitment',
  'objection_delta',
  'dispo_alignment',
] as const

const METRIC_LABELS: Record<string, string> = {
  talk_ratio: 'Talk ratio',
  longest_monologue: 'Monologue',
  next_step_commitment: 'Next-step',
  objection_delta: 'Objection',
  dispo_alignment: 'Dispo',
  localize_within_15s: 'Localize <15s',
  free_word_density: 'Free-word density',
  time_to_pitch: 'Time-to-pitch',
  name_personalization_rate: 'Name personalization',
  seven_step_coverage: 'Seven-step coverage',
}

// 10 metrics in display order — matches backend `aggregate.METRIC_ORDER`.
const TREND_METRIC_ORDER = [
  'talk_ratio',
  'longest_monologue',
  'next_step_commitment',
  'objection_delta',
  'dispo_alignment',
  'localize_within_15s',
  'free_word_density',
  'time_to_pitch',
  'name_personalization_rate',
  'seven_step_coverage',
] as const

interface TrendDay {
  date: string
  n_calls: number
  metrics: Record<string, number>
}

interface MetricTrendsResponse {
  vici_user: string
  window_days: number
  by_date: TrendDay[]
}

// Fetch metric trends for the agent summary mini-charts. Mirrors
// `fetchScorecard` style — uses the same Bearer key pattern as the rest of
// the api client. Treats any non-2xx as "no data" so the section degrades
// silently if the backend isn't deployed yet.
async function fetchMetricTrends(
  viciUser: string
): Promise<MetricTrendsResponse | null> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const key = getApiKey()
  if (key) headers.Authorization = `Bearer ${key}`
  let res: Response
  try {
    res = await fetch(
      `/api/agents/${encodeURIComponent(viciUser)}/metric-trends?days=30`,
      { headers }
    )
  } catch {
    return null
  }
  if (!res.ok) return null
  try {
    return (await res.json()) as MetricTrendsResponse
  } catch {
    return null
  }
}

function formatPct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '--'
  return `${(rate * 100).toFixed(2)}%`
}

// Pull tier/rates from either summary top-level or nested agent (backend rollout).
function pickTierFields(data: AgentSummary) {
  return {
    tier: (data.tier ?? data.agent.tier ?? null) as Tier | null,
    tier_name: data.tier_name ?? data.agent.tier_name ?? null,
    per_dial_rate: data.per_dial_rate ?? data.agent.per_dial_rate ?? null,
    per_contact_rate: data.per_contact_rate ?? data.agent.per_contact_rate ?? null,
    gate_failures: data.gate_failures ?? null,
  }
}

// Optional scorecard endpoint. Endpoint may not exist yet — we treat any
// non-2xx (including 404) as "no scorecard" and skip the section silently.
// Returns either { markdown: string } or a raw markdown string body.
interface ScorecardResponse {
  markdown?: string | null
  text?: string | null
}

async function fetchScorecard(viciUser: string): Promise<string | null> {
  const headers: Record<string, string> = { Accept: 'text/markdown, application/json;q=0.9, */*;q=0.5' }
  const key = getApiKey()
  if (key) headers.Authorization = `Bearer ${key}`
  let res: Response
  try {
    res = await fetch(`/api/agents/${encodeURIComponent(viciUser)}/scorecard`, { headers })
  } catch {
    return null
  }
  if (!res.ok) return null
  const ct = res.headers.get('content-type') || ''
  try {
    if (ct.includes('application/json')) {
      const j = (await res.json()) as ScorecardResponse | string
      if (typeof j === 'string') return j || null
      if (j && typeof j === 'object') return j.markdown ?? j.text ?? null
      return null
    }
    const txt = await res.text()
    return txt.trim() ? txt : null
  } catch {
    return null
  }
}

export function AgentSummaryPage() {
  const { vici_user } = useParams<{ vici_user: string }>()
  const { data, isLoading, error } = useQuery({
    queryKey: ['agent-summary', vici_user],
    queryFn: () => api.agents.summary(vici_user ?? ''),
    enabled: Boolean(vici_user),
  })

  // Scorecard is best-effort — we don't surface fetch errors to the user,
  // we just hide the section if no markdown comes back.
  const { data: scorecard } = useQuery({
    queryKey: ['agent-scorecard', vici_user],
    queryFn: () => fetchScorecard(vici_user ?? ''),
    enabled: Boolean(vici_user),
    retry: false,
    staleTime: 60_000,
  })

  // Metric trends — last 30 days, per-metric pass-rate timeseries. Best-effort:
  // backend may not have shipped the route yet, in which case we render an
  // empty-state placeholder for each metric.
  const { data: trends } = useQuery({
    queryKey: ['agent-metric-trends', vici_user],
    queryFn: () => fetchMetricTrends(vici_user ?? ''),
    enabled: Boolean(vici_user),
    retry: false,
    staleTime: 60_000,
  })

  // Coaching plan — best-effort, hides silently on 404 (route may not be
  // deployed yet, same pattern as scorecard/trends).
  const { data: coaching } = useQuery({
    queryKey: ['agent-coaching', vici_user],
    queryFn: () => fetchCoaching(vici_user ?? ''),
    enabled: Boolean(vici_user),
    retry: false,
    staleTime: 60_000,
  })

  if (!vici_user) return <p className="text-sm text-red-300">No agent specified.</p>

  const tierFields = data ? pickTierFields(data) : null

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-2">
        <User className="h-5 w-5 text-indigo-400" />
        <h1 className="text-xl font-semibold">{vici_user}</h1>
        {data?.agent.full_name && (
          <span className="text-sm text-slate-400">{data.agent.full_name}</span>
        )}
        {tierFields && tierFields.tier !== null && (
          <TierBadge tier={tierFields.tier} tierName={tierFields.tier_name} />
        )}
        <Link
          to={`/calls?agent=${encodeURIComponent(vici_user)}`}
          className="ml-auto rounded border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800"
        >
          All calls →
        </Link>
      </header>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-900 bg-red-950/40 p-3 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{(error as Error).message}</span>
        </div>
      )}

      {isLoading || !data ? (
        <div className="h-80 animate-pulse rounded bg-slate-800/60" />
      ) : (
        <>
          <section className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <Stat label="Total calls" value={data.counts.total} />
            <Stat label="Scored" value={data.counts.scored} />
            <Stat
              label="Mismatches"
              value={data.counts.mismatches}
              sub={data.counts.scored ? `${Math.round((data.counts.mismatches / data.counts.scored) * 100)}%` : undefined}
            />
            <Stat
              label="Avg overall"
              value={
                data.agent.avg_overall !== null && data.agent.avg_overall !== undefined
                  ? data.agent.avg_overall.toFixed(1)
                  : '--'
              }
              sub="out of 10.0"
            />
            <Stat
              label="Per-dial rate"
              value={formatPct(tierFields?.per_dial_rate)}
              sub="bookings ÷ dials"
            />
            <Stat
              label="Per-contact rate"
              value={formatPct(tierFields?.per_contact_rate)}
              sub="bookings ÷ contacts"
            />
          </section>

          <GateFailures failures={tierFields?.gate_failures} />

          {coaching && <CoachingTab plan={coaching} />}

          <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h2 className="mb-3 text-sm font-semibold text-slate-200">
              Metric Trends (30 days)
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {TREND_METRIC_ORDER.map((metric) => {
                // Build per-metric series (sparse: only days with calls).
                const series =
                  trends?.by_date.map((d) => ({
                    date: d.date,
                    rate: d.metrics?.[metric] ?? 0,
                  })) ?? []
                return (
                  <MetricTrendChart
                    key={metric}
                    label={METRIC_LABELS[metric] ?? metric}
                    series={series}
                  />
                )
              })}
            </div>
          </section>

          {scorecard && (
            <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200">
                <FileText className="h-4 w-4 text-indigo-400" />
                Scorecard
              </h2>
              <pre className="whitespace-pre-wrap break-words rounded bg-slate-950/60 p-3 font-mono text-xs leading-relaxed text-slate-300">
                {scorecard}
              </pre>
            </section>
          )}

          <section className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
              <h2 className="mb-3 text-sm font-semibold text-slate-200">Score distribution</h2>
              <ScoreDistribution buckets={data.score_distribution} />
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
              <h2 className="mb-3 text-sm font-semibold text-slate-200">Per-metric pass rate</h2>
              <div className="space-y-2">
                {METRIC_ORDER.map((m) => {
                  const rate = (data.per_metric_pass_rate as Record<string, number>)[m]
                  return (
                    <div key={m} className="flex items-center gap-3">
                      <span className="w-28 text-xs text-slate-300">{METRIC_LABELS[m]}</span>
                      <div className="flex-1">
                        {rate === undefined ? (
                          <span className="text-xs text-slate-600">no data</span>
                        ) : (
                          <PassRateBar rate={rate} />
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
            <h2 className="mb-3 text-sm font-semibold text-slate-200">Dispo breakdown</h2>
            <div className="flex flex-wrap gap-2">
              {data.dispo_breakdown.length === 0 ? (
                <p className="text-xs text-slate-500">No dispo data.</p>
              ) : (
                data.dispo_breakdown.map((d) => (
                  <div
                    key={d.dispo}
                    className="flex items-center gap-2 rounded border border-slate-800 bg-slate-950 px-2 py-1 text-xs"
                  >
                    <DispoBadge dispo={d.dispo} />
                    <span className="font-mono text-slate-300">{d.count}</span>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="grid gap-4 md:grid-cols-2">
            <CallList
              title="Top 5"
              icon={<TrendingUp className="h-4 w-4 text-emerald-400" />}
              rows={data.top5}
            />
            <CallList
              title="Bottom 5"
              icon={<TrendingDown className="h-4 w-4 text-red-400" />}
              rows={data.bottom5}
            />
          </section>
        </>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string
  value: string | number
  sub?: string
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-100">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  )
}

function ScoreDistribution({
  buckets,
}: {
  buckets: Array<{ bucket: string; count: number }>
}) {
  if (!buckets.length) {
    return <p className="text-xs text-slate-500">No scored calls yet.</p>
  }
  const max = Math.max(...buckets.map((b) => b.count), 1)
  return (
    <div className="space-y-1.5">
      {buckets.map((b) => {
        const pct = (b.count / max) * 100
        return (
          <div key={b.bucket} className="flex items-center gap-3">
            <span className="w-10 font-mono text-xs text-slate-400">{b.bucket}</span>
            <div className="h-3 flex-1 overflow-hidden rounded bg-slate-800">
              <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-10 text-right font-mono text-xs text-slate-300">{b.count}</span>
          </div>
        )
      })}
    </div>
  )
}

function CallList({
  title,
  icon,
  rows,
}: {
  title: string
  icon: React.ReactNode
  rows: CallRow[]
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-200">
        {icon}
        {title}
      </h2>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-500">No calls.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to={`/calls/${r.id}`}
                className="flex items-center gap-2 rounded px-2 py-1 text-xs hover:bg-slate-800/60"
              >
                <ScoreBar value={r.overall} showLabel={false} className="shrink-0" />
                <span className="font-mono text-slate-300">
                  {r.overall !== null ? r.overall.toFixed(1) : '--'}
                </span>
                <DispoBadge dispo={r.vici_disposition} />
                <span className="text-slate-500">
                  {new Date(r.call_started_at).toLocaleDateString()}
                </span>
                <DispoMismatchBadge alignment={r.dispo_alignment} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
